// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, ApiErrorType } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

export enum InquiryStatus {
  STARTED = 'started',
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

const getInquiryQueryKey = 'get-inquiry';
const getInquiryQuery = async (inquiryId: string, codingContext: CodingContext | undefined) => {
  const inquiryResponse = await apiClient.get<{
    inquiryId: string;
    status: InquiryStatus;
    isAssociatedWithSession: boolean;
  }>(`inquiry/${inquiryId}`, codingContext);

  if (inquiryResponse.status === InquiryStatus.ERROR) {
    throw { error: { code: 'inquiry-error', message: 'Inquiry failed. Unable to proceed with enrollment.', type: ApiErrorType.APP } } as ApiErrorPayloadCommon;
  }

  return inquiryResponse;
};

export const useGetInquiryQuery = (options: {
  onSuccess: (inquiryId: string, status: InquiryStatus, isAssociatedWithSession: boolean) => void;
}) => {
  const [searchParams] = useSearchParams();
  const [inquiryId, setInquiryId] = useState<string>('');
  const { codingContext } = usePlatform();
  const query = useQuery<
    {
      inquiryId: string;
      status: InquiryStatus;
    },
    ApiErrorPayloadCommon
  >({
    queryKey: [getInquiryQueryKey, inquiryId],
    queryFn: () =>
      getInquiryQuery(inquiryId, codingContext).then((res) => {
        options.onSuccess(res.inquiryId, res.status, res.isAssociatedWithSession);
        return res;
      }),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!inquiryId,
    retry: false,
  });

  useEffect(() => {
    setInquiryId(searchParams.get('inquiry-id') ?? '');
  }, [searchParams]);

  return {
    inquiry: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
};
